<template>
  <div class="block md:w-10/12 w-full bg-white rounded-3xl md:mx-12 px-12">
    <Modal
        v-if="state.isOpen"
        :confirmButton="state.popUpConfirm ?? 'Oké'"
        @confirm="onModalConfirm"
    >
      <div class="text-center">{{ state.message }}</div>
    </Modal>
    <TwoFactorAuth
        isLogin
        v-if="state.isTFAOpen"
        :email="state.email"
        @submit="onTFASubmit"
        @close="state.isTFAOpen = false"
        @onErrorUpdate="(value: any) => { state.isLoginError = value }"
        ref="TFARef"
    />
    <div class="grid grid-cols-2 gap-x-6 gap-y-9 py-8">
      <div class="col-span-2 md:col-span-1">
        <div
            class="font-bold text-label-color-4 synvest-title"
        >
          Log in op MijnSynVest
        </div>
        <div class="text-second">
          <span class="block mt-10 underline">
            <RouterLink to="/account-aanmaken" class="text-base"
            >Nog geen klant?</RouterLink
            >
          </span>
          <span class="block mt-10 underline">
            <RouterLink to="/wachtwoord-vergeten" class="text-base"
            >Wachtwoord vergeten/aanvragen?</RouterLink
            >
          </span>
        </div>
      </div>

      <form class="col-span-2 md:col-span-1" @submit.prevent>
        <div class="pb-3" v-if="state.isLoginError && !state.isPasswordExpired">
          <p class="text-xs text-red-500">Inloggegevens komen niet overeen</p>
        </div>
        <div class="w-full md:max-w-sm pb-2">
          <TextInput
              id="email"
              label="E-mailadres"
              v-model="state.email"
              :errors="vuelidate.email.$errors"
          />
        </div>
        <div class="w-full md:max-w-sm pb-2">
          <PasswordInput
              id="password"
              label="Wachtwoord"
              v-model="state.password"
              :errors="vuelidate.password.$errors"
          />
        </div>

        <CheckboxInput
            id="remember"
            compact
            label="Onthoud e-mailadres"
            v-model="state.isRememberEmail"
        />
        <Button
            class="mt-6"
            type="primary"
            :disabled="vuelidate.$invalid"
            @click="login"
        >Inloggen</Button
        >
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from "@/components/Button.vue";
import CheckboxInput from "@/components/FormInputs/CheckboxInput.vue";
import Modal from "@/components/Modal.vue";
import PasswordInput from "@/components/FormInputs/PasswordInput.vue";
import TextInput from "@/components/FormInputs/TextInput.vue";
import TwoFactorAuth from "@/views/Common/TwoFactorAuth.vue";
import router from "@/router";
import useVuelidate from "@vuelidate/core";
import request, { requestMethodType } from "@/plugins/request";
import { onBeforeMount, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import {
  showGlobalError,
  showLoading,
  addNotification,
  getIsOpenDomain,
  setTwoFaTokenMethod,
} from "@/store/mainInterface";
import { required, email } from "@/plugins/validators";

const store = useStore();
const TFARef = ref<InstanceType<typeof TwoFactorAuth>>(null);
const advisorUrl = import.meta.env.VITE_ADVISOR_URL;

const state = reactive<{
  email: string;
  isLoginError: boolean;
  isOpen: boolean;
  isPasswordExpired: boolean;
  isRememberEmail: boolean;
  isTFAOpen: boolean;
  message: string;
  password: string;
  popUpConfirm: string;
  userName: string;
  emailToken: string;
  twoFaTokenMethod: string;
}>({
  email: localStorage.getItem("rememberedEmail") || "",
  isLoginError: false,
  isOpen: false,
  isPasswordExpired: false,
  isRememberEmail: !!localStorage.getItem("rememberedEmail"),
  isTFAOpen: false,
  message: "",
  password: "",
  popUpConfirm: null,
  userName: null,
  emailToken: null,
  twoFaTokenMethod: '',
});

const rules = {
  email: { required, email },
  password: { required },
};

const vuelidate = useVuelidate(rules, state, { $autoDirty: true });

watch(
    () => state.isRememberEmail,
    (remember) => {
      if (remember) {
        localStorage.setItem("rememberedEmail", state.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    },
);

function onModalConfirm() {
  state.isOpen = false;
  /* Checking if user's password is expired */
  if (state.isPasswordExpired) {
    router.push({ path: "/wachtwoord-vergeten" });
  }
}

async function login() {
  showLoading(true);
  showGlobalError(false);
  state.isLoginError = false;

  try {
    const response = await request(
        "/api/tokens",
        requestMethodType.POST,
        { email: state.email, password: state.password },
        { headers: {} },
    );
    const data = response.data;

    if (data) {
      state.userName = data.username;
      state.emailToken = data.email_token;
      setTwoFaTokenMethod(data.method);
      store.commit("setMobile", data.mobile);
      state.isTFAOpen = true;
    }
  } catch (e) {
    if (e?.data?.errors?.ErrorMessage) {
      const messages: string[] = e.data.errors.ErrorMessage;
      if (
          messages.some(
              (message) =>
                  message ===
                  "Het wachtwoord verlopen is en dat er een nieuw ww opgegeven moet worden.",
          )
      ) {
        state.isPasswordExpired = true;
        state.message = "Uw wachtwoord is verlopen.";
        state.popUpConfirm = "Nieuw wachtwoord aanvragen ";
        state.isOpen = true;
      } else {
        messages.forEach((message) =>
            addNotification({ title: "Fout", message: message }),
        );
      }
    }

    if (e.status === 400) {
      state.isLoginError = true;
    }
  }

  showLoading(false);
  showGlobalError(true);
}

async function onTFASubmit(code: string) {
  state.isLoginError = false;

  const payload = {
    code: code,
    emailToken: state.emailToken,
  };

  showLoading(true);

  try {
    const TFAResponse = await request(
        "/api/tokens/two-fa",
        requestMethodType.POST,
        payload,
        { headers: {}, auth: true },
    );

    const data = TFAResponse.data;
    if (data.mobile){
      store.commit("setMobile", data.mobile);
    }
    if (data) {

      const response = await request(
          "/api/users/me",
          requestMethodType.GET,
          null,
          { headers: {}, auth: true },
      );

      if (response.data?.data) {
        store.commit("setAuth", { user: response.data.data });
        store.commit("setAuthUsername", state.userName);
        state.isTFAOpen = false;

        if (response.data.data.role === "admin") {
          await router.push({ path: "/admin" });
        } else {
          await router.push({ path: "/" });
        }
      }
    }
  } catch (e) {
    if (e.data?.message === "The code is incorrect.") {
      TFARef.value.setErrorMessage(
          "De verificatiecode is onjuist, probeer het opnieuw",
      );
    } else {
      addNotification({
        title: "Error",
        message: "De verificatiecode is onjuist, probeer het opnieuw",
      });
    }

    if (e.status === 400) {
      state.isLoginError = true;
      addNotification({
        title: "Error",
        message:
            "De verificatiecode is onjuist of verlopen. Probeer het opnieuw.",
      });
    }
  }
  showLoading(false);
}

onBeforeMount(async () => {
  store.commit("setAuth", { user: null });

  if (!getIsOpenDomain()) {
    store.commit("resetState", {});
  }

  const params: { token?: string } = useRoute().query;

  if (params.token) {

    showGlobalError(false);
    showLoading(true);
    try {
      const verificationResponse = await request(
          "/api/verification",
          requestMethodType.PUT,
          { token: params.token },
          { headers: {}, fullError: true },
      );
      if (verificationResponse.status == 204) {
        state.isOpen = true;
        state.message = 'Uw account is geactiveerd. U kunt nu inloggen op MijnSynVest.';
      }
    } catch (e) {
      if (e.data.errors?.ErrorMessage?.[0]) {
        state.isOpen = true;
        state.message = e.data.errors.ErrorMessage[0];
      }
    }
    showLoading(false);
    showGlobalError(true);
  }
});
</script>

<style scoped lang="scss">
@import "@env_styles/variables";

.text-second {
  color: $text-color-second;
}

.synvest-title {
  font-size: 1.1rem !important;
}
</style>
